import { useFormikContext } from "formik";
import { type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import Grid from "@mui/material/Grid";

import type { IncidentReport } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from "../../hooks/useThrottledSectionUpdate";
import { useIncidentReportContext } from "../../IncidentReportContextProvider";
import { ChooseYesNoOther } from "components/CriticalIncident/components/ChooseYesNoOther";
import { TextBox } from "components/Shared/Inputs";
import { FormControlWrapper } from "components/Shared";
import { IN_PERSON_COUNSELLING_MODALITY_CODE } from "constants/modalities";

import "./IncidentDocumentation.scss";

const MAX_CHARS = 2000;

export const IncidentDocumentation: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, handleChange, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const { isAU } = useIncidentReportContext();
  const isInPersonModality = values.interventionSummary?.supportModality === IN_PERSON_COUNSELLING_MODALITY_CODE;
  const throttledUpdateReport = useThrottledSectionUpdate('incidentDocumentation', values.incidentDocumentation);

  const updateSelection = (field: string, value: string) => {
    setFieldValue(field, value);
    const fieldToUpdate = field.split('.').slice(1).join('.');
    throttledUpdateReport({ [fieldToUpdate]: value });
  }

  const makeChangeHandler = (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(event);
    const fieldToUpdate = fieldName.split('.').slice(1).join('.');
    throttledUpdateReport({ [fieldToUpdate]: event.target.value });
  }

  // Positive-integer fields are left uncontrolled-to-empty while typing (see the `?? 0` on their
  // `value` below) so backspacing to clear the field isn't immediately overwritten by a forced
  // fallback. This commits a default of 0 once the user leaves the field empty, so Formik never
  // ends up holding '' or null for these fields.
  const makeIntegerBlurHandler = (fieldName: string) => (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value === '') {
      setFieldValue(fieldName, 0);
      const fieldToUpdate = fieldName.split('.').slice(1).join('.');
      throttledUpdateReport({ [fieldToUpdate]: 0 });
    }
  }

  return translationsReady && (
    <div className="incident-documentation">
      <h2>{t('incidentDocumentation')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={<>{t('preparation_meeting_with_contact')}:</>}
            name="incidentDocumentation.preparationMeetingWithContact"
            type="radio.yesnoother"
            fullWidth
          >
            <ChooseYesNoOther
              label={<>{t('preparation_meeting_with_contact')}:</>}
              name="incidentDocumentation.preparationMeetingWithContact"
              value={values.incidentDocumentation?.preparationMeetingWithContact}
              onChange={(value) => updateSelection('incidentDocumentation.preparationMeetingWithContact', value)}
            />
          </FormControlWrapper>
          {values.incidentDocumentation?.preparationMeetingWithContact === 'O' && (
            <FormControlWrapper
              isReadonly={isReadonly}
              label={<>{t('preparation_meeting_description')}:</>}
              name="incidentDocumentation.preparationMeetingWithContactOtherReason"
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={`${t('preparation_meeting_description')}:`}
                required
                name="incidentDocumentation.preparationMeetingWithContactOtherReason"
                value={values.incidentDocumentation?.preparationMeetingWithContactOtherReason}
                onChange={makeChangeHandler('incidentDocumentation.preparationMeetingWithContactOtherReason')}
                slotProps={{
                  input: { maxLength: MAX_CHARS }
                }}
                error={touched.incidentDocumentation?.preparationMeetingWithContactOtherReason && Boolean(errors?.incidentDocumentation?.preparationMeetingWithContactOtherReason)}
                helperText={touched.incidentDocumentation?.preparationMeetingWithContactOtherReason ? t(errors?.incidentDocumentation?.preparationMeetingWithContactOtherReason || ' '): ''}
              />
            </FormControlWrapper>
          )}
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('were_employees_aware_and_engaged_in_services')}
            name="incidentDocumentation.wereEmployeesAwareAndEngaged"
            type="radio.yesnoother"
            fullWidth
          >
            <ChooseYesNoOther
              label={t('were_employees_aware_and_engaged_in_services')}
              name="incidentDocumentation.wereEmployeesAwareAndEngaged"
              required
              value={values.incidentDocumentation?.wereEmployeesAwareAndEngaged}
              onChange={(value) => updateSelection('incidentDocumentation.wereEmployeesAwareAndEngaged', value)}
              error={touched.incidentDocumentation?.wereEmployeesAwareAndEngaged && Boolean(errors?.incidentDocumentation?.wereEmployeesAwareAndEngaged)}
              helperText={touched.incidentDocumentation?.wereEmployeesAwareAndEngaged ? t(errors?.incidentDocumentation?.wereEmployeesAwareAndEngaged || ' '): ''}
            />
          </FormControlWrapper>
          {values.incidentDocumentation?.wereEmployeesAwareAndEngaged === 'O' && (
            <FormControlWrapper
              isReadonly={isReadonly}
              name="incidentDocumentation.wereEmployeesAwareAndEngagedOtherReason"
              label={t('employee_reactions_detail')}
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={`${t('employee_reactions_detail')}:`}
                required
                name="incidentDocumentation.wereEmployeesAwareAndEngagedOtherReason"
                value={values.incidentDocumentation?.wereEmployeesAwareAndEngagedOtherReason}
                onChange={makeChangeHandler('incidentDocumentation.wereEmployeesAwareAndEngagedOtherReason')}
                slotProps={{
                  input: { maxLength: MAX_CHARS }
                }}
                error={touched.incidentDocumentation?.wereEmployeesAwareAndEngagedOtherReason && Boolean(errors?.incidentDocumentation?.wereEmployeesAwareAndEngagedOtherReason)}
                helperText={touched.incidentDocumentation?.wereEmployeesAwareAndEngagedOtherReason && t(errors.incidentDocumentation?.wereEmployeesAwareAndEngagedOtherReason || ' ')}
              />
            </FormControlWrapper>
          )}
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('were_referrals_extended')}
            name="incidentDocumentation.wereReferralsExtended"
            type="radio.yesnoother"
            fullWidth
          >
            <ChooseYesNoOther
              label={t('were_referrals_extended')}
              name="incidentDocumentation.wereReferralsExtended"
              required
              value={values.incidentDocumentation?.wereReferralsExtended}
              onChange={(value) => updateSelection('incidentDocumentation.wereReferralsExtended', value)}
              error={touched.incidentDocumentation?.wereReferralsExtended && Boolean(errors?.incidentDocumentation?.wereReferralsExtended)}
              helperText={touched.incidentDocumentation?.wereReferralsExtended ? t(errors?.incidentDocumentation?.wereReferralsExtended || ' '): ''}
            />
          </FormControlWrapper>
          {values.incidentDocumentation?.wereReferralsExtended === 'O' && (
            <FormControlWrapper
              isReadonly={isReadonly}
              name="incidentDocumentation.wereReferralsExtendedOtherReason"
              label={t('referrals_to_eap_description')}
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={`${t('referrals_to_eap_description')}:`}
                required
                name="incidentDocumentation.wereReferralsExtendedOtherReason"
                value={values.incidentDocumentation?.wereReferralsExtendedOtherReason}
                onChange={(e) => {
                  handleChange(e);
                  throttledUpdateReport({ wereReferralsExtendedOtherReason: e.target.value });
                }}
                slotProps={{
                  input: { maxLength: MAX_CHARS }
                }}
                error={touched.incidentDocumentation?.wereReferralsExtendedOtherReason && Boolean(errors?.incidentDocumentation?.wereReferralsExtendedOtherReason)}
                helperText={touched.incidentDocumentation?.wereReferralsExtendedOtherReason ? t(errors.incidentDocumentation?.wereReferralsExtendedOtherReason || ' ') : ''}
              />
            </FormControlWrapper>
          )}
        </Grid>
        {(!isAU || isInPersonModality) && (
          <Grid item sm={12}>
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t('number_of_walk_abouts')}
              name="incidentDocumentation.noOfWalkAbouts"
              fullWidth
              className="oneline_text_box"
            >
              <TextBox
                type="positive-integer"
                label={t('number_of_walk_abouts')}
                name="incidentDocumentation.noOfWalkAbouts"
                value={values.incidentDocumentation?.noOfWalkAbouts ?? 0}
                onChange={makeChangeHandler('incidentDocumentation.noOfWalkAbouts')}
                onBlur={makeIntegerBlurHandler('incidentDocumentation.noOfWalkAbouts')}
              />
            </FormControlWrapper>
            {values.incidentDocumentation?.noOfWalkAbouts > 0 && (
              <FormControlWrapper
                isReadonly={isReadonly}
                label={t('walk_about_description')}
                name="incidentDocumentation.walkAboutDescription"
                fullWidth
              >
                <TextBox
                  multiline
                  minRows={4}
                  maxRows={6}
                  label={`${t('walk_about_description')}:`}
                  required
                  name="incidentDocumentation.walkAboutDescription"
                  value={values.incidentDocumentation?.walkAboutDescription}
                  onChange={(e) => {
                    handleChange(e);
                    throttledUpdateReport({ walkAboutDescription: e.target.value });
                  }}
                  slotProps={{
                    input: { maxLength: MAX_CHARS }
                  }}
                  error={touched.incidentDocumentation?.walkAboutDescription && Boolean(errors?.incidentDocumentation?.walkAboutDescription)}
                  helperText={touched.incidentDocumentation?.walkAboutDescription ? t(errors.incidentDocumentation?.walkAboutDescription || ' '): ''}
                />
              </FormControlWrapper>
            )}
          </Grid>
        )}
        {!isAU && (
          <Grid item sm={12}>
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t('number_of_outreach_requests')}
              name="incidentDocumentation.noOfOutreachRequests"
              fullWidth
              className="oneline_text_box"
            >
              <TextBox
                type="positive-integer"
                label={t('number_of_outreach_requests')}
                name="incidentDocumentation.noOfOutreachRequests"
                value={values.incidentDocumentation?.noOfOutreachRequests ?? 0}
                onChange={makeChangeHandler('incidentDocumentation.noOfOutreachRequests')}
                onBlur={makeIntegerBlurHandler('incidentDocumentation.noOfOutreachRequests')}
              />
            </FormControlWrapper>
            {values.incidentDocumentation?.noOfOutreachRequests > 0 && (
              <FormControlWrapper
                isReadonly={isReadonly}
                label={t('outreach_description')}
                name="incidentDocumentation.outreachDescription"
                fullWidth
              >
                <TextBox
                  multiline
                  minRows={4}
                  maxRows={6}
                  label={`${t('outreach_description')}:`}
                  required
                  name="incidentDocumentation.outreachDescription"
                  value={values.incidentDocumentation?.outreachDescription}
                  onChange={(e) => {
                    handleChange(e);
                    throttledUpdateReport({ outreachDescription: e.target.value });
                  }}
                  slotProps={{
                    input: { maxLength: MAX_CHARS }
                  }}
                  error={touched.incidentDocumentation?.outreachDescription && Boolean(errors?.incidentDocumentation?.outreachDescription)}
                  helperText={touched.incidentDocumentation?.outreachDescription ? t(errors.incidentDocumentation?.outreachDescription || ' '): ''}
                />
              </FormControlWrapper>
            )}
          </Grid>
        )}
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('number_of_consultations')}
            name="incidentDocumentation.noOfConsultations"
            fullWidth
            className="oneline_text_box"
          >
            <TextBox
              type="positive-integer"
              label={t('number_of_consultations')}
              name="incidentDocumentation.noOfConsultations"
              value={values.incidentDocumentation?.noOfConsultations ?? 0}
              onChange={makeChangeHandler('incidentDocumentation.noOfConsultations')}
              onBlur={makeIntegerBlurHandler('incidentDocumentation.noOfConsultations')}
            />
          </FormControlWrapper>
          {values.incidentDocumentation?.noOfConsultations > 0 && (
            <FormControlWrapper
              isReadonly={isReadonly}
              label={t('consultation_description')}
              name="incidentDocumentation.consultationDescription"
              fullWidth
            >
              <TextBox
                multiline
                minRows={4}
                maxRows={6}
                label={`${t('consultation_description')}:`}
                required
                name="incidentDocumentation.consultationDescription"
                value={values.incidentDocumentation?.consultationDescription}
                onChange={(e) => {
                  handleChange(e);
                  throttledUpdateReport({ consultationDescription: e.target.value });
                }}
                slotProps={{
                  input: { maxLength: MAX_CHARS }
                }}
                error={touched.incidentDocumentation?.consultationDescription && Boolean(errors?.incidentDocumentation?.consultationDescription)}
                helperText={touched.incidentDocumentation?.consultationDescription ? t(errors.incidentDocumentation?.consultationDescription || ' '): ''}
              />
            </FormControlWrapper>
          )}
        </Grid>
      </Grid>
    </div>
  );
};
